import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { publicApi, fileUrl } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import PreviewBanner from "@/components/public/PreviewBanner";
import Reveal from "@/components/public/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, Check, Compass, Search, ShieldCheck, PackageCheck, Gem } from "lucide-react";
import { usePageIntro, PAGE_INTROS } from "@/lib/cms";

const READINESS = [
  { value: "now", label: "Ready to acquire now" },
  { value: "30d", label: "Within 30 days" },
  { value: "90d", label: "Within 90 days" },
  { value: "researching", label: "Exploring / no rush" },
];

const STEPS = [
  { icon: Compass, t: "Private Consultation", d: "We learn the exact piece, the specification, and the story you want it to tell." },
  { icon: Search, t: "The Discreet Hunt", d: "Our global network is put to work — dealers, collectors, and quiet channels others can't reach." },
  { icon: ShieldCheck, t: "Authentication", d: "Every candidate is inspected and verified before it is ever presented to you." },
  { icon: PackageCheck, t: "White-Glove Delivery", d: "Insured, discreet, and documented — with a full digital dossier for your archive." },
];

const EMPTY = {
  full_name: "", email: "", phone: "", brand_wanted: "", model_wanted: "", reference_number: "",
  budget_min: "", budget_max: "", buying_readiness: "", special_requests: "", consent_contact: false, vip_signup: true,
};

const inputCls = "bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]";

function Field({ label, children, hint }) {
  return (
    <div className="space-y-2">
      <Label className="text-[var(--text-2)] text-sm">{label}</Label>
      {children}
      {hint && <p className="text-xs text-[var(--muted)]">{hint}</p>}
    </div>
  );
}

export default function Bespoke() {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [sourced, setSourced] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [intro, preview] = usePageIntro(PAGE_INTROS.bespoke.key, PAGE_INTROS.bespoke.defaults);

  useEffect(() => {
    const badgeFor = (s) => (s === "sold" ? "Sourced" : s === "reserved" ? "Reserved" : "In Collection");
    const rank = { sold: 0, reserved: 1, available: 2, incoming: 3, authentication_pending: 4 };
    publicApi.get("/public/watches").then((r) => {
      const list = r.data || [];
      const picked = [...list]
        .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9))
        .slice(0, 6)
        .map((w) => ({
          id: w.id, brand: w.brand, model: w.model, reference_number: w.reference_number,
          image: (w.public_photos && w.public_photos[0]) || null, badge: badgeFor(w.status),
        }));
      setSourced(picked);
    }).catch(() => { /* showcase is optional */ });
  }, []);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async () => {
    if (!form.full_name || !form.email) { toast.error("Name and email are required"); return; }
    if (!form.consent_contact) { toast.error("Please agree to be contacted"); return; }
    setBusy(true);
    try {
      const notes = `[Bespoke request] ${form.special_requests || ""}`.trim();
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || null,
        brand_wanted: form.brand_wanted || null,
        model_wanted: form.model_wanted || null,
        reference_number: form.reference_number || null,
        item_type: "Bespoke sourcing",
        buying_readiness: form.buying_readiness || null,
        budget_min: form.budget_min === "" ? null : Number(form.budget_min),
        budget_max: form.budget_max === "" ? null : Number(form.budget_max),
        special_requests: notes,
        consent_contact: true,
        vip_signup: !!form.vip_signup,
      };
      await publicApi.post("/public/watch-request", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="bespoke-confirmation">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#c9a86a] text-[#c9a86a] mb-6"><Check size={28} /></div>
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">COMMISSION RECEIVED</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--text)]">The search is under way.</h1>
          <p className="text-[var(--text-2)] mt-6 leading-relaxed">
            A member of our sourcing desk will reach out personally to refine the brief and begin the hunt.
            Every piece we present is authenticated before it changes hands.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/watches")} className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none px-8 py-6" data-testid="bespoke-browse">Browse the Collection</Button>
            <Link to="/"><Button variant="outline" className="border-[var(--border-2)] text-[var(--text-2)] rounded-none px-8 py-6">Back to Home</Button></Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden min-h-[74vh] flex items-end" data-testid="bespoke-hero">
        <div className="absolute inset-0">
          <img
            src={fileUrl(intro.image)}
            alt=""
            className="w-full h-full object-cover opacity-[0.55]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg)]/20 via-transparent to-[var(--bg)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-transparent to-transparent" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(70% 55% at 50% 75%, rgba(201,168,106,0.07), transparent 72%)" }} />
        </div>
        <Reveal className="relative w-full max-w-4xl mx-auto px-5 pb-20 md:pb-28 text-center">
          <div className="text-[#c9a86a] tracking-[0.4em] text-[11px] md:text-xs mb-5 uppercase [text-shadow:0_1px_18px_var(--bg)]">Private Sourcing</div>
          <div className="mx-auto mb-7 h-px w-16 bg-gradient-to-r from-transparent via-[#c9a86a]/70 to-transparent" aria-hidden="true" />
          <h1 className="font-serif text-[var(--text)] text-[2.5rem] leading-[1.06] md:text-[4rem] md:leading-[1.03] tracking-[-0.01em] [text-shadow:0_2px_36px_var(--bg)]">
            The bespoke commission.
            <span className="block mt-2 md:mt-3 italic text-[#e0c693]">For the piece that isn&rsquo;t listed.</span>
          </h1>
          <p className="text-[var(--text-2)] mt-7 max-w-xl mx-auto text-base md:text-lg leading-relaxed">
            Tell us the grail. We put a discreet global network to work — sourcing, authenticating,
            and delivering the exact reference, specification, and condition you have in mind.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={scrollToForm}
              data-testid="bespoke-begin-cta"
              className="group inline-flex items-center justify-center gap-3 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold uppercase tracking-[0.15em] text-sm px-9 py-4 rounded-none transition-[background-color,transform] duration-200 active:translate-y-px shadow-[0_16px_40px_-14px_rgba(201,168,106,0.6)]"
            >
              Begin Your Commission
              <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1.5" />
            </button>
            <Button variant="outline" onClick={() => navigate("/watches")} className="border-[var(--border-2)] text-[var(--text-2)] rounded-none px-9 py-4 h-auto uppercase tracking-[0.15em] text-sm bg-transparent hover:bg-[var(--panel-2)]">
              View the Collection
            </Button>
          </div>
        </Reveal>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 py-20">
        <Reveal className="text-center mb-14">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3 uppercase">The Process</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[var(--text)]">Four movements, one result.</h2>
          <div className="mx-auto mt-6 flex items-center justify-center gap-3 w-full max-w-xs">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-[#c9a86a]/45" />
            <Gem size={14} className="text-[#c9a86a]/80" />
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-[#c9a86a]/45" />
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.t} style={{ transitionDelay: `${i * 80}ms` }}>
              <div className="group h-full rounded-xl border border-[var(--border-lch)] bg-[var(--panel-2)] p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-[#c9a86a]/40">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#c9a86a]/40 text-[#c9a86a]">
                    <s.icon size={20} />
                  </span>
                  <span className="font-serif text-2xl text-[var(--border-2)] group-hover:text-[#c9a86a]/50 transition-colors">0{i + 1}</span>
                </div>
                <h3 className="mt-5 text-lg font-medium text-[var(--text)]">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Recently sourced showcase */}
      {sourced.length > 0 && (
        <section className="border-t border-[var(--border-lch)] bg-[var(--bg-2)]">
          <div className="max-w-6xl mx-auto px-5 py-20">
            <Reveal className="text-center mb-12">
              <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3 uppercase">Recently Sourced</div>
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--text)]">Commissions we&rsquo;ve delivered</h2>
              <p className="text-[var(--text-2)] mt-3 max-w-xl mx-auto">A glimpse of pieces we&rsquo;ve hunted down and placed with collectors. Yours could be next.</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bespoke-sourced">
              {sourced.map((w, i) => (
                <Reveal key={w.id} style={{ transitionDelay: `${i * 70}ms` }}>
                  <Link to={`/watches/${w.id}`} data-testid="sourced-card"
                    className="group block overflow-hidden rounded-xl border border-[var(--border-lch)] bg-[var(--panel-2)] transition-transform duration-200 hover:-translate-y-1 hover:border-[#c9a86a]/40">
                    <div className="relative aspect-[4/3] overflow-hidden bg-[var(--bg)]">
                      <div className="absolute inset-0 flex items-center justify-center text-[var(--muted)]"><Gem size={22} /></div>
                      {w.image && (
                        <img src={w.image} alt={`${w.brand} ${w.model}`} loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="relative h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      )}
                      <span className="absolute top-3 left-3 rounded-full bg-[var(--bg)]/80 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-wider text-[#c9a86a] border border-[#c9a86a]/30">{w.badge}</span>
                    </div>
                    <div className="p-4">
                      <div className="text-sm font-medium text-[var(--text)]">{w.brand} {w.model}</div>
                      <div className="text-xs text-[var(--muted)] mt-0.5">{w.reference_number ? `Ref ${w.reference_number}` : "Sourced to order"}</div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* The brief / form */}
      <section ref={formRef} className="border-t border-[var(--border-lch)] bg-[var(--bg-2)] scroll-mt-28">
        <div className="max-w-3xl mx-auto px-5 py-20">
          <Reveal className="text-center mb-10">
            <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3 uppercase">The Brief</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[var(--text)]">Commission your piece</h2>
            <p className="text-[var(--text-2)] mt-3">A few essentials to begin. Our concierge will refine the rest with you personally.</p>
          </Reveal>

          <div className="bg-[var(--panel-2)] border border-[var(--border-2)] rounded-xl p-6 md:p-8 space-y-5" data-testid="bespoke-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name *"><Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="bes-name" /></Field>
              <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="bes-email" /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="bes-phone" /></Field>
              <Field label="Timeline"><Select value={form.buying_readiness} onValueChange={(v) => set("buying_readiness", v)}><SelectTrigger className={inputCls} data-testid="bes-timeline"><SelectValue placeholder="Select…" /></SelectTrigger><SelectContent>{READINESS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Brand"><Input value={form.brand_wanted} onChange={(e) => set("brand_wanted", e.target.value)} placeholder="e.g. Patek Philippe" className={inputCls} data-testid="bes-brand" /></Field>
              <Field label="Model"><Input value={form.model_wanted} onChange={(e) => set("model_wanted", e.target.value)} placeholder="e.g. Nautilus" className={inputCls} data-testid="bes-model" /></Field>
              <Field label="Reference (if known)"><Input value={form.reference_number} onChange={(e) => set("reference_number", e.target.value)} placeholder="e.g. 5711/1A" className={inputCls} data-testid="bes-ref" /></Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Budget Minimum (USD)"><Input type="number" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} className={inputCls} data-testid="bes-budget-min" /></Field>
              <Field label="Budget Maximum (USD)"><Input type="number" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} className={inputCls} data-testid="bes-budget-max" /></Field>
            </div>

            <Field label="The Details" hint="Dial, condition, box & papers, provenance — anything that matters to you.">
              <Textarea rows={4} value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} placeholder="Describe the exact piece you're after…" className={inputCls} data-testid="bes-notes" />
            </Field>

            <label className="flex items-start gap-3 cursor-pointer pt-1">
              <Checkbox checked={form.consent_contact} onCheckedChange={(v) => set("consent_contact", !!v)} data-testid="bes-consent" className="mt-0.5" />
              <span className="text-sm text-[var(--text-2)]">I agree to be contacted by Le Collectif Horloger about my commission. *</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={form.vip_signup} onCheckedChange={(v) => set("vip_signup", !!v)} data-testid="bes-vip" />
              <span className="text-sm text-[var(--text-2)]">Also add me to the VIP list for first access to new arrivals</span>
            </label>

            <div className="pt-4 border-t border-[var(--border-2)] flex justify-end">
              <Button type="button" onClick={submit} disabled={busy} data-testid="bespoke-submit" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none px-8">
                {busy ? "Submitting…" : "Submit Commission"}
              </Button>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-[var(--muted)]">
            Prefer the detailed sourcing form? <Link to="/request" className="text-[#c9a86a] hover:underline">Open the full Request a Watch brief</Link>.
          </p>
        </div>
      </section>
      {preview && <PreviewBanner testId="bespoke-preview-banner" />}
    </PublicLayout>
  );
}
